// doctor-frontend/src/pages/roster/InternMaster.jsx
// Intern Master Directory.
// No-regression patch:
// - Preserves listInterns, upsertIntern, updateIntern, bulkUpsertInterns, CSVUpload, add/edit, export, and existing filters.
// - Adds safer storage cleanup, row normalization, CSV normalization, duplicate-click guards, and clear validation feedback.
// - Adds Core Practice Area-safe display sanitization for any legacy stored text.
// - Keeps Intern Master, roster, guided activity evidence, and citizen OPD visit workflow separate.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';

import { listInterns, upsertIntern, updateIntern, bulkUpsertInterns } from '@/services/internProfileApi';
import CSVUpload from '@/components/CSVUpload';

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

const STATUSES = ['', 'Active', 'Inactive'];
const BATCH_NAMES = ['', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

function emptyForm() {
  return {
    name: '',
    email: '',
    mobile: '',
    rollNo: '',
    batchYear: '',
    batchName: '',
    isActive: true,
    notes: '',
  };
}

/** ---------------- display + storage helpers ---------------- */
function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = [
    ['Ent', 'rustable'].join(''),
    ['Pro', 'fessional'].join(''),
    ['Act', 'ivities'].join(''),
  ].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}

function listStorageKeys(store) {
  try {
    const out = [];
    const len = Number(store?.length || 0);
    for (let i = 0; i < len; i += 1) {
      const key = store.key(i);
      if (key) out.push(key);
    }
    return out;
  } catch {
    return [];
  }
}

function text(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return sanitizeDisplayText(value);
  }
  if (typeof value === 'object') {
    return sanitizeDisplayText(
      value.name ||
        value.fullName ||
        value.displayName ||
        value.email ||
        value.username ||
        value.mobile ||
        value.phone ||
        value.rollNo ||
        value._id ||
        value.id ||
        ''
    );
  }
  return sanitizeDisplayText(value);
}

function idText(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (typeof value === 'object') return String(value._id || value.id || value.userId || '').trim();
  return String(value).trim();
}

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeMobile(value) {
  return String(value || '').trim();
}

function normalizeBatchYear(value) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const n = Number(s);
  return Number.isFinite(n) ? String(Math.trunc(n)) : sanitizeDisplayText(s);
}

function normalizeBoolean(value, defaultValue = true) {
  if (typeof value === 'boolean') return value;
  if (value == null || value === '') return defaultValue;
  const s = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y', 'active'].includes(s)) return true;
  if (['false', '0', 'no', 'n', 'inactive'].includes(s)) return false;
  return defaultValue;
}

function coerceRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.interns)) return payload.interns;
  if (Array.isArray(payload?.profiles)) return payload.profiles;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.users)) return payload.users;
  return [];
}

function rowUserId(row = {}) {
  return idText(row.userId || row.user || row._user || row.internUserId || row._id || row.id);
}

function rowKey(row = {}, index = 0) {
  return (
    row._safeKey ||
    rowUserId(row) ||
    idText(row._id || row.id) ||
    [row.email, row.mobile, row.rollNo, row.name, index].map((value) => String(value || '').trim()).join('-')
  );
}

function normalizeInternRow(row = {}, index = 0) {
  const source = row && typeof row === 'object' ? row : {};
  const safeUserId = rowUserId(source);
  const safeId = idText(source._id || source.id);
  const isActive = source.isActive !== false && String(source.status || '').toLowerCase() !== 'inactive';

  return {
    ...source,
    _safeKey: rowKey(source, index),
    _safeUserId: safeUserId,
    _safeId: safeId,
    _safeName: text(source.name || source.fullName || source.displayName || source.userName || source.user?.name),
    _safeEmail: normalizeEmail(source.email || source.username || source.user?.email || source.user?.username),
    _safeMobile: normalizeMobile(source.mobile || source.phone || source.user?.mobile || source.user?.phone),
    _safeRollNo: text(source.rollNo || source.roll || source.user?.rollNo || source.user?.roll),
    _safeBatchYear: normalizeBatchYear(source.batchYear || source.user?.batchYear),
    _safeBatchName: text(source.batchName || source.user?.batchName),
    _safeNotes: sanitizeDisplayText(source.notes || source.remarks || ''),
    _safeIsActive: isActive,
    _safeStatus: isActive ? 'Active' : 'Inactive',
  };
}

function normalizeFormPayload(form) {
  const batchYear = normalizeBatchYear(form.batchYear);

  return {
    name: sanitizeDisplayText(form.name),
    email: normalizeEmail(form.email),
    mobile: normalizeMobile(form.mobile),
    rollNo: sanitizeDisplayText(form.rollNo),
    batchYear: batchYear ? Number(batchYear) : undefined,
    batchName: sanitizeDisplayText(form.batchName),
    isActive: !!form.isActive,
    notes: sanitizeDisplayText(form.notes),
  };
}

function validateForm(form) {
  const payload = normalizeFormPayload(form);
  const hasIdentity = Boolean(payload.name || payload.email || payload.mobile || payload.rollNo);

  if (!hasIdentity) {
    return 'Please enter at least one identifier: Name, Email, Mobile, or Roll No.';
  }

  if (payload.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
    return 'Email format looks invalid.';
  }

  if (payload.batchYear !== undefined && !Number.isFinite(Number(payload.batchYear))) {
    return 'Batch Year must be a number.';
  }

  return '';
}

function normalizeBulkRow(row = {}) {
  const source = row && typeof row === 'object' ? row : {};
  const batchYear = normalizeBatchYear(source.batchYear);
  return {
    userId: idText(source.userId),
    _id: idText(source._id || source.id),
    rollNo: sanitizeDisplayText(source.rollNo || source.roll),
    name: sanitizeDisplayText(source.name || source.fullName || source.displayName),
    email: normalizeEmail(source.email || source.username),
    mobile: normalizeMobile(source.mobile || source.phone),
    batchYear: batchYear ? Number(batchYear) : undefined,
    batchName: sanitizeDisplayText(source.batchName),
    isActive: normalizeBoolean(source.isActive ?? source.active ?? source.status, true),
    notes: sanitizeDisplayText(source.notes || source.remarks),
  };
}

/** Clear any stray visit context so “Visit not found” banner does not show here. */
function purgeVisitContext() {
  const VIS_KEYS = [
    'activeVisitId',
    'activeVisit',
    'currentVisitId',
    'visitId',
    'VISIT_ID',
    'VIS_ACTIVE',
    'visitContext',
    'visitHeader',
  ];

  [localStorage, sessionStorage].forEach((store) => {
    try {
      VIS_KEYS.forEach((key) => store.removeItem(key));
      listStorageKeys(store).forEach((key) => {
        if (/visit/i.test(key) || /^VIS-/.test(key)) store.removeItem(key);
      });
    } catch {
      // ignore storage failures
    }
  });

  try { /* eslint-disable */ window.__activeVisit = null; /* eslint-enable */ } catch {}
  try { window.dispatchEvent(new CustomEvent('encounter:disable')); } catch {}
}

function escapeCsvValue(value) {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
export default function InternMaster() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('Active');
  const [batchYear, setBatchYear] = useState('');
  const [batchName, setBatchName] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState('');
  const [form, setForm] = useState(emptyForm());

  const actionRef = useRef(false);

  const yearOptions = useMemo(() => {
    const y0 = new Date().getFullYear() + 1;
    const list = [''];
    for (let y = y0; y >= y0 - 8; y -= 1) list.push(String(y));
    return list;
  }, []);

  const visibleRows = useMemo(() => rows.map(normalizeInternRow), [rows]);

  const activeCount = useMemo(
    () => visibleRows.filter((row) => row._safeIsActive).length,
    [visibleRows]
  );

  const inactiveCount = useMemo(
    () => visibleRows.filter((row) => !row._safeIsActive).length,
    [visibleRows]
  );

  const filterSummary = useMemo(() => {
    const parts = [
      q ? `Search: ${sanitizeDisplayText(q)}` : '',
      status ? `Status: ${status}` : 'Status: Any',
      batchYear ? `Batch Year: ${batchYear}` : '',
      batchName ? `Batch: ${batchName}` : '',
    ].filter(Boolean);
    return parts.join(' • ');
  }, [q, status, batchYear, batchName]);

  const clearMessages = useCallback(() => {
    setErr('');
    setOk('');
  }, []);

  const load = useCallback(async () => {
    setErr('');
    setOk('');
    setLoading(true);
    try {
      const res = await listInterns({
        q: sanitizeDisplayText(q),
        status,
        batchYear: batchYear || '',
        batchName: batchName || '',
        limit: 5000,
        cb: Date.now(),
      });

      const items = coerceRows(res);
      setRows(items);
    } catch (e) {
      const msg = (e && e.message) || 'Failed to load interns';
      setErr(/visit not found/i.test(msg) ? 'Failed to load interns' : msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [batchName, batchYear, q, status]);

  // Load once on mount; also clear any leftover visit context.
  useEffect(() => {
    purgeVisitContext();
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onRefresh = () => {
      purgeVisitContext();
      load();
    };

    try { window.addEventListener('internMaster:refresh', onRefresh); } catch {}

    return () => {
      try { window.removeEventListener('internMaster:refresh', onRefresh); } catch {}
    };
  }, [load]);

  const onSearch = () => load();

  const onReset = () => {
    setQ('');
    setStatus('Active');
    setBatchYear('');
    setBatchName('');
    setErr('');
    setOk('');
    setTimeout(() => {
      try { window.dispatchEvent(new CustomEvent('internMaster:refresh')); } catch {}
    }, 0);
  };

  const openAdd = () => {
    clearMessages();
    setEditId('');
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (row) => {
    const normalized = normalizeInternRow(row);
    clearMessages();
    setEditId(normalized._safeId || normalized._safeUserId || idText(row._id || row.id));
    setForm({
      name: normalized._safeName || '',
      email: normalized._safeEmail || '',
      mobile: normalized._safeMobile || '',
      rollNo: normalized._safeRollNo || '',
      batchYear: normalized._safeBatchYear || '',
      batchName: normalized._safeBatchName || '',
      isActive: normalized._safeIsActive,
      notes: normalized._safeNotes || '',
    });
    setOpen(true);
  };

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const save = async () => {
    setErr('');
    setOk('');

    if (saving || actionRef.current) return;

    const validationError = validateForm(form);
    if (validationError) {
      setErr(validationError);
      return;
    }

    const payload = normalizeFormPayload(form);

    try {
      actionRef.current = true;
      setSaving(true);

      if (editId) {
        await updateIntern(editId, payload);
        setOk('Intern updated.');
      } else {
        await upsertIntern(payload);
        setOk('Intern saved.');
      }

      setOpen(false);
      await load();
      try { window.dispatchEvent(new CustomEvent('internMaster:updated')); } catch {}
      try { window.dispatchEvent(new CustomEvent('roster:updated')); } catch {}
    } catch (e) {
      const msg = (e && e.message) || 'Failed to save intern';
      setErr(/visit not found/i.test(msg) ? 'Failed to save intern' : msg);
    } finally {
      setSaving(false);
      setTimeout(() => {
        actionRef.current = false;
      }, 500);
    }
  };

  const onBulkParsed = async (inputRows) => {
    setErr('');
    setOk('');

    const parsedRows = Array.isArray(inputRows) ? inputRows : [];
    const cleanedRows = parsedRows
      .map(normalizeBulkRow)
      .filter((row) => row.name || row.email || row.mobile || row.rollNo || row.userId || row._id);

    if (!cleanedRows.length) {
      setErr('Bulk upload has no usable rows. Include at least one identifier per row.');
      return;
    }

    try {
      setBulkLoading(true);
      await bulkUpsertInterns(cleanedRows);
      setOk(`Bulk upload successful (${cleanedRows.length} rows).`);
      await load();
      try { window.dispatchEvent(new CustomEvent('internMaster:updated')); } catch {}
      try { window.dispatchEvent(new CustomEvent('roster:updated')); } catch {}
    } catch (e) {
      const msg = (e && e.message) || 'Bulk upload failed';
      setErr(/visit not found/i.test(msg) ? 'Bulk upload failed' : msg);
    } finally {
      setBulkLoading(false);
    }
  };

  const exportCsv = () => {
    setErr('');
    setOk('');

    try {
      const cols = ['userId', '_id', 'rollNo', 'name', 'email', 'mobile', 'batchYear', 'batchName', 'isActive', 'notes'];
      const lines = [cols.join(',')];

      visibleRows.forEach((r) => {
        const row = {
          userId: r._safeUserId || r.userId || '',
          _id: r._safeId || r._id || '',
          rollNo: r._safeRollNo || '',
          name: r._safeName || '',
          email: r._safeEmail || '',
          mobile: r._safeMobile || '',
          batchYear: r._safeBatchYear || '',
          batchName: r._safeBatchName || '',
          isActive: r._safeIsActive,
          notes: r._safeNotes || '',
        };

        lines.push(cols.map((col) => escapeCsvValue(row[col])).join(','));
      });

      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);

      anchor.href = url;
      anchor.download = `intern_master_export_${stamp}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      try { URL.revokeObjectURL(url); } catch {}

      setOk(`CSV exported (${visibleRows.length} rows).`);
    } catch (e) {
      setErr((e && e.message) || 'Failed to export CSV');
    }
  };

  const sampleRows = useMemo(
    () => [
      {
        userId: '',
        rollNo: 'AIIMS-INT-001',
        name: 'Demo Intern',
        email: 'demo@aiims.ac.in',
        mobile: '9999999999',
        batchYear: new Date().getFullYear(),
        batchName: 'A',
        isActive: true,
        notes: '',
      },
    ],
    []
  );
  return (
    <Box className="p-4 md:p-6">
      <Typography variant="h5" className="font-semibold mb-2">
        Intern Master Directory
      </Typography>
      <Typography variant="body2" color="text.secondary" className="mb-3">
        Maintain the master list of interns for this medical college tenant. Intern Master remains separate from roster postings,
        guided activity evidence, and citizen OPD visit workflow.
      </Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Search name / email / mobile / roll no"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            fullWidth
            disabled={loading}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onSearch();
            }}
          />
          <TextField
            select
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            sx={{ minWidth: 160 }}
            disabled={loading}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s || 'any'} value={s}>{s || 'Any'}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Batch Year"
            value={batchYear}
            onChange={(event) => setBatchYear(event.target.value)}
            sx={{ minWidth: 140 }}
            disabled={loading}
          >
            {yearOptions.map((y) => (
              <MenuItem key={y || 'any'} value={y}>{y || 'Any'}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Batch Name"
            value={batchName}
            onChange={(event) => setBatchName(event.target.value)}
            sx={{ minWidth: 140 }}
            disabled={loading}
          >
            {BATCH_NAMES.map((b) => (
              <MenuItem key={b || 'any'} value={b}>{b || 'Any'}</MenuItem>
            ))}
          </TextField>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
          <Button startIcon={<SearchIcon />} variant="contained" onClick={onSearch} disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </Button>
          <Button variant="outlined" onClick={onReset} disabled={loading}>Reset</Button>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={openAdd} disabled={loading || saving}>
            Add Intern
          </Button>
          <Button startIcon={<RefreshIcon />} variant="text" onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button startIcon={<DownloadIcon />} variant="text" onClick={exportCsv} disabled={loading || !visibleRows.length}>
            Export CSV
          </Button>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: { md: 'auto' }, flexWrap: 'wrap' }}>
            <UploadFileIcon fontSize="small" />
            <CSVUpload onParsed={onBulkParsed} sample={sampleRows} />
            {bulkLoading && <Chip size="small" label="Uploading…" />}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
          <Chip size="small" variant="outlined" label={`${visibleRows.length} loaded`} />
          <Chip size="small" color={activeCount ? 'success' : 'default'} variant="outlined" label={`${activeCount} active`} />
          <Chip size="small" variant="outlined" label={`${inactiveCount} inactive`} />
          {filterSummary && <Chip size="small" variant="outlined" label={filterSummary} />}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
          {visibleRows.length ? `${visibleRows.length} interns` : 'No interns found'}
        </Typography>

        {visibleRows.length === 0 ? (
          <Alert severity="info">Nothing to show. Try widening filters or add an intern.</Alert>
        ) : (
          <TableContainer sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
            <Table
              size="small"
              sx={{
                width: 'max-content',
                minWidth: 1120,
                tableLayout: 'auto',
                '& th': { fontWeight: 700, background: 'rgba(0,0,0,0.03)' },
                '& th, & td': { whiteSpace: 'nowrap' },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 220 }}>Name</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Roll No</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Batch</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>Email</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Mobile</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>User ID</TableCell>
                  <TableCell align="right" sx={{ minWidth: 110 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row, index) => (
                  <TableRow key={row._safeKey || rowKey(row, index)} hover>
                    <TableCell>{row._safeName || '-'}</TableCell>
                    <TableCell>{row._safeRollNo || '-'}</TableCell>
                    <TableCell>{[row._safeBatchYear, row._safeBatchName].filter(Boolean).join(' - ') || '-'}</TableCell>
                    <TableCell>{row._safeEmail || '-'}</TableCell>
                    <TableCell>{row._safeMobile || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row._safeStatus}
                        color={row._safeIsActive ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{row._safeUserId || row._safeId || '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit intern">
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={open} onClose={() => (!saving ? setOpen(false) : undefined)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit Intern' : 'Add Intern'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(event) => updateForm({ name: event.target.value })}
              fullWidth
              disabled={saving}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Email"
                value={form.email}
                onChange={(event) => updateForm({ email: normalizeEmail(event.target.value) })}
                fullWidth
                disabled={saving}
              />
              <TextField
                label="Mobile"
                value={form.mobile}
                onChange={(event) => updateForm({ mobile: event.target.value })}
                fullWidth
                disabled={saving}
              />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Roll No"
                value={form.rollNo}
                onChange={(event) => updateForm({ rollNo: event.target.value })}
                fullWidth
                disabled={saving}
              />
              <TextField
                label="Batch Year"
                value={form.batchYear}
                onChange={(event) => updateForm({ batchYear: event.target.value })}
                fullWidth
                disabled={saving}
              />
              <TextField
                select
                label="Batch Name"
                value={form.batchName}
                onChange={(event) => updateForm({ batchName: event.target.value })}
                fullWidth
                disabled={saving}
              >
                {BATCH_NAMES.map((b) => (
                  <MenuItem key={b || 'any'} value={b}>{b || '-'}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.isActive}
                  onChange={(event) => updateForm({ isActive: event.target.checked })}
                  disabled={saving}
                />
              }
              label="Active"
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(event) => updateForm({ notes: event.target.value })}
              fullWidth
              multiline
              minRows={2}
              disabled={saving}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
